import { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import dayjs from 'dayjs'
import 'dayjs/locale/pt'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import { Loader2Icon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { TimeInput } from '@/components/ui/time-input'
import AutocompleteInput from '../autocomplete-input'
import { useNotificationsProvider } from '../../context/NotificationsContext'
import { useSetTagsForEntityMutation } from '../../services/api'
import { TagsMultiSelect } from '../tags-multi-select'

ScheduleInspectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onScheduled: PropTypes.func.isRequired,
  defaultDate: PropTypes.instanceOf(Date),
  controllers: PropTypes.array,
  loadingControllers: PropTypes.bool,
  scheduleInspectionFunction: PropTypes.func.isRequired,
}

export default function ScheduleInspectionDialog({
  open,
  onClose,
  onScheduled,
  defaultDate = null,
  controllers = [],
  loadingControllers = false,
  scheduleInspectionFunction,
}) {
  const { t } = useTranslation()
  const { showNotification } = useNotificationsProvider()
  const [setTags] = useSetTagsForEntityMutation()

  const locale = useMemo(() => {
    const browserLocale = (navigator.language || navigator.userLanguage).substring(0, 2)
    if (['pt', 'fr', 'en'].includes(browserLocale)) {
      dayjs.locale(browserLocale)
      return browserLocale
    }
    dayjs.locale('en')
    return 'en'
  }, [])

  const schema = z.object({
    selectedResource: z
      .any()
      .refine((v) => !!v && !!v.id, t('schedule-inspection-dialog.errors.resource-required')),
    selectedFlow: z
      .any()
      .refine((v) => !!v && !!v.id, t('schedule-inspection-dialog.errors.flow-required')),
    selectedControllerId: z
      .string()
      .min(1, t('schedule-inspection-dialog.errors.controller-required')),
    controlDate: z
      .any()
      .refine((v) => !!v, t('schedule-inspection-dialog.errors.date-required')),
    selectedTags: z.array(z.any()),
  })

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      selectedResource: null,
      selectedFlow: null,
      selectedControllerId: '',
      controlDate: defaultDate ? dayjs(defaultDate) : dayjs(),
      selectedTags: [],
    },
  })

  const loading = form.formState.isSubmitting

  // Re-seed on open. If `defaultDate` lands at midnight (calendar
  // cell click), bump it to the current wall-clock so the time slot
  // doesn't read 00:00.
  useEffect(() => {
    if (!open) return
    let dateValue = defaultDate ? dayjs(defaultDate) : dayjs()
    if (defaultDate && dateValue.hour() === 0 && dateValue.minute() === 0) {
      const now = dayjs()
      dateValue = dateValue.hour(now.hour()).minute(now.minute())
    }
    form.reset({
      selectedResource: null,
      selectedFlow: null,
      selectedControllerId: '',
      controlDate: dateValue,
      selectedTags: [],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultDate])

  const onOpenChange = (next) => {
    if (!next) {
      if (loading) return
      onClose()
    }
  }

  const onSubmit = async (values) => {
    try {
      const controlDateISOString = values.controlDate.toISOString()
      const scheduled = await scheduleInspectionFunction({
        resourceId: values.selectedResource.id,
        flowId: values.selectedFlow.id,
        assignedToUserId: values.selectedControllerId,
        controlDate: controlDateISOString,
      })

      if (values.selectedTags.length > 0 && scheduled?.id) {
        try {
          await setTags({
            taggableType: 'Control',
            taggableId: scheduled.id,
            tagIds: values.selectedTags.map((tag) => tag.id),
          }).unwrap()
        } catch (tagErr) {
          console.error('Failed to attach tags to scheduled inspection:', tagErr)
        }
      }

      onScheduled()
      onClose()
    } catch (err) {
      console.error('Error scheduling inspection:', err)
      const errorCode = err?.response?.data?.errorCode
      const keyByCode = {
        RESOURCE_NOT_FOUND: 'schedule-inspection-dialog.errors.resource-not-found',
        FLOW_NOT_FOUND: 'schedule-inspection-dialog.errors.flow-not-found',
        USER_NOT_FOUND: 'schedule-inspection-dialog.errors.controller-not-found',
        PAST_DATE_NOT_ALLOWED: 'schedule-inspection-dialog.errors.past-date',
      }
      showNotification(
        'error',
        t(keyByCode[errorCode] ?? 'schedule-inspection-dialog.errors.api-error'),
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
        onInteractOutside={(e) => { if (loading) e.preventDefault() }}
        onEscapeKeyDown={(e) => { if (loading) e.preventDefault() }}
      >
        <DialogHeader>
          <DialogTitle>{t('schedule-inspection-dialog.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('schedule-inspection-dialog.title')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="selectedResource"
              render={({ field, fieldState }) => (
                <FormItem>
                  <AutocompleteInput
                    resource="resource/name-autocomplete"
                    label={t('schedule-inspection-dialog.resource')}
                    value={field.value}
                    onChange={(v) => field.onChange(v)}
                    required
                    error={!!fieldState.error}
                    refreshOnInputChange
                    canDelete
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="selectedFlow"
              render={({ field, fieldState }) => (
                <FormItem>
                  <AutocompleteInput
                    resource="flows/autocomplete"
                    label={t('schedule-inspection-dialog.flow')}
                    value={field.value}
                    onChange={(v) => field.onChange(v)}
                    required
                    error={!!fieldState.error}
                    refreshOnInputChange
                    canDelete
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="selectedControllerId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="after:ml-0.5 after:text-destructive after:content-['*']">
                    {t('schedule-inspection-dialog.controller')}
                  </FormLabel>
                  <Select
                    value={field.value || undefined}
                    onValueChange={field.onChange}
                    disabled={loadingControllers}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={t('schedule-inspection-dialog.controller')}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {controllers.map((controller) => (
                        <SelectItem key={controller.id} value={String(controller.id)}>
                          {controller.firstName} {controller.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="selectedTags"
              render={({ field }) => (
                <FormItem>
                  <TagsMultiSelect
                    value={field.value}
                    onChange={field.onChange}
                    label={t('schedule-inspection-dialog.tags', 'Tags')}
                    placeholder={t('tags.multi-select.placeholder')}
                  />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="controlDate"
              render={({ field }) => (
                <FormItem>
                  <div className="grid grid-cols-7 gap-2">
                    <div className="col-span-4">
                      <DatePicker
                        label={t('schedule-inspection-dialog.date')}
                        value={field.value}
                        onChange={(newDate) => {
                          if (newDate && field.value) {
                            field.onChange(
                              newDate
                                .hour(field.value.hour())
                                .minute(field.value.minute()),
                            )
                          } else {
                            field.onChange(newDate)
                          }
                        }}
                        format={locale === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'}
                        locale={locale}
                      />
                    </div>
                    <div className="col-span-3">
                      <TimeInput
                        label={t('schedule-inspection-dialog.time')}
                        value={field.value}
                        onChange={(newTime) => {
                          if (newTime && field.value) {
                            field.onChange(
                              field.value
                                .hour(newTime.hour())
                                .minute(newTime.minute()),
                            )
                          } else if (newTime) {
                            field.onChange(newTime)
                          }
                        }}
                      />
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null}
                {t('schedule-inspection-dialog.confirm')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
